import {
  IconArrowOpenDownLine,
  IconBulletListLine,
  IconCodeLine,
  IconNumberedListLine,
  Menu,
} from "@instructure/ui";
import { $createCodeNode } from "@lexical/code";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { $createHeadingNode, $createQuoteNode } from "@lexical/rich-text";
import { $setBlocksType } from "@lexical/selection";
import {
  $createParagraphNode,
  $getSelection,
  $isRangeSelection,
} from "lexical";
import { useTranslation } from "react-i18next";

const MenuDropdown = ({ editor, blockType, disabled }) => {
  const { t } = useTranslation();

  const blockTypeToBlockName = {
    paragraph: t("rte.paragraph"),
    h1: t("rte.heading1"),
    h2: t("rte.heading2"),
    ul: t("rte.bulletedList"),
    ol: t("rte.numberedList"),
    quote: t("rte.quote"),
    code: t("rte.codeBlock"),
  };
  const blockTypeToBlockIcon = {
    paragraph: <span className="icon paragraph" />,
    h1: <span className="icon large-heading" />,
    h2: <span className="icon small-heading" />,
    ul: <IconBulletListLine />,
    ol: <IconNumberedListLine />,
    quote: <span className="icon quote" />,
    code: <IconCodeLine />,
  };

  const formatParagraph = () => {
    if (blockType !== "paragraph") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  };

  const formatLargeHeading = () => {
    if (blockType !== "h1") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode("h1"));
        }
      });
    }
  };

  const formatSmallHeading = () => {
    if (blockType !== "h2") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode("h2"));
        }
      });
    }
  };

  const formatBulletList = () => {
    if (blockType !== "ul") {
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND);
    }
  };

  const formatNumberedList = () => {
    if (blockType !== "ol") {
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND);
    } else {
      editor.dispatchCommand(REMOVE_LIST_COMMAND);
    }
  };

  const formatQuote = () => {
    if (blockType !== "quote") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  };

  const formatCode = () => {
    if (blockType !== "code") {
      editor.update(() => {
        const selection = $getSelection();

        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createCodeNode());
        }
      });
    }
  };

  return (
    <Menu
      trigger={
        <button
          className="toolbar-item block-controls"
          aria-label={t("rte.formattingOptions")}
          disabled={disabled}
          style={disabled ? { opacity: 0.4 } : undefined}
        >
          <span className="icon">{blockTypeToBlockIcon[blockType]}</span>
          <span className="text">{blockTypeToBlockName[blockType]}</span>
          <IconArrowOpenDownLine size="x-small" />
        </button>
      }
    >
      <Menu.Item onClick={formatParagraph}>
        <div className="menu-item">
          <span className="icon paragraph" />
          {t("rte.paragraph")}
        </div>
      </Menu.Item>
      <Menu.Item onClick={formatLargeHeading}>
        <div className="menu-item">
          <span className="icon large-heading" />
          {t("rte.heading1")}
        </div>
      </Menu.Item>
      <Menu.Item onClick={formatSmallHeading}>
        <div className="menu-item">
          <span className="icon small-heading" />
          {t("rte.heading2")}
        </div>
      </Menu.Item>
      <Menu.Item onClick={formatBulletList}>
        <div className="menu-item">
          <IconBulletListLine />
          {t("rte.bulletedList")}
        </div>
      </Menu.Item>
      <Menu.Item onClick={formatNumberedList}>
        <div className="menu-item">
          <IconNumberedListLine />
          {t("rte.numberedList")}
        </div>
      </Menu.Item>
      <Menu.Item onClick={formatQuote}>
        <div className="menu-item">
          <span className="icon quote" />
          {t("rte.quote")}
        </div>
      </Menu.Item>
      <Menu.Item onClick={formatCode}>
        <div className="menu-item">
          <IconCodeLine /> {t("rte.codeBlock")}
        </div>
      </Menu.Item>
    </Menu>
  );
};

export const TextPlugin = ({ editor, blockType, disabled = false }) => {
  return (
    <MenuDropdown editor={editor} blockType={blockType} disabled={disabled} />
  );
};
